import React, { useState, useEffect } from "react";
import { GroupedTasksByCategory, RiskJobRecord } from "../../../../models/job-task.interface";
import useApiService from "../../../../hooks/useApiService";
import { formDataType } from "../AddSite";
import styles from "../add-site.module.css";
import { Checkbox } from "../../../../components/Shared/Forms";
import { Divider } from "primereact/divider";
import ModernLoader from "../../../../components/Shared/ModernLoader";

interface JobTasksProps {
  handleChange: (name: string, value: number[]) => void;
  formData: formDataType
}

export function JobTaskWizard({ handleChange, formData }: JobTasksProps) {
  const [groupedTasks, setGroupedTasks] = useState<GroupedTasksByCategory[]>([]);
  const [categories, setCategories] = useState<{ name: string; id: number }[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<number[]>([]);
  const [selectedTasks, setSelectedTasks] = useState<RiskJobRecord[]>([]);
  const [availableTasks, setAvailableTasks] = useState<RiskJobRecord[]>([]);
  const [allCategoriesSelected, setAllCategoriesSelected] = useState<boolean>(false);
  const [allTasksSelected, setAllTasksSelected] = useState<boolean>(false);

  useEffect(() => {
    setAvailableTasks(deriveAvailableTasks(groupedTasks, selectedCategories));
  }, [groupedTasks, selectedCategories]);

  // If "all tasks" was selected and new available tasks appear (e.g., a category added)
  // that are not in selectedTasks, unset the allTasksSelected flag while preserving selections.
  useEffect(() => {
    if (!allTasksSelected) return;
    const selectedIds = new Set(selectedTasks.map(t => t.id));
    const allIncluded = availableTasks.every(t => selectedIds.has(t.id));
    if (!allIncluded) {
      setAllTasksSelected(false);
    }
  }, [availableTasks, selectedTasks, allTasksSelected]);

  useEffect(() => {
    handleChange(
      "job_task_categories",
      selectedCategories
    );
  }, [selectedCategories])

  useEffect(() => {
    if (formData?.job_task_categories && selectedCategories?.length === 0) {
      setSelectedCategories(formData.job_task_categories);
      formData?.job_task_categories.forEach(async categoryId => {
        await handleCategoriesChange(categoryId);
      });
    }
  }, [formData?.job_task_categories]);

  useEffect(() => {
    let params: any = {
      rows: 100,
      page: 0,
      bypassSite: true,
    }
    if (formData?.industry) {
      params.industry = formData.industry;
    }
    fetchClientCategories({
      fetchParams: params
    });
  }, [formData?.industry]);
  // industry

  // Restore selected tasks when formData has job_task values
  useEffect(() => {
    if (formData?.job_task && formData.job_task.length > 0 && selectedTasks.length === 0) {
      // Find tasks that match the saved IDs from availableTasks
      const tasksToRestore = availableTasks.filter(task =>
        formData.job_task?.includes(task.id)
      );
      if (tasksToRestore.length > 0) {
        setSelectedTasks(tasksToRestore);
      }
    }
  }, [formData?.job_task, availableTasks]);

  // Update "select all" states based on current selections
  useEffect(() => {
    if (categories.length > 0) {
      setAllCategoriesSelected(
        selectedCategories.length === categories.length
      );
    }
  }, [selectedCategories, categories]);

  useEffect(() => {
    if (availableTasks.length > 0) {
      setAllTasksSelected(
        selectedTasks.length === availableTasks.length && selectedTasks.length > 0
      );
    }
  }, [selectedTasks, availableTasks]);

  function handleSelectionChange(e: RiskJobRecord) {
    const isSelected = selectedTasks.includes(e);

    if (isSelected) {
      setSelectedTasks(selectedTasks.filter(task => task.id !== e.id));
      handleChange(
        "job_task",
        selectedTasks.map(task => task.id).filter(task => task !== e.id)
      );
      return;
    }
    setSelectedTasks([...selectedTasks, e]);
    const newTaskArray = [...selectedTasks.map(task => task.id), e.id];
    handleChange("job_task", newTaskArray);
  }

  const { loading: clientCategoriesLoading, fetchData: fetchClientCategories } = useApiService<{ id: number; name: string }[]>({
    method: "GET",
    fetchOnMount: false,
    url: "risk-task-category",
    apiName: "core",
    onSuccess: data => {
      setCategories(data);
    },
    onError(error) {
      console.log("Error fetching categories == ", error);
      setCategories([]);
    },
  });

  const { loading: loadingCategoryTasks, fetchData: getTasksByCategory } = useApiService<
    RiskJobRecord[]
  >({
    url: "risk-job-task",
    method: "GET",
    fetchOnMount: false,
    params: {
      bypassSite: true
    },
    onSuccess: data => {
      const grouped = groupTasksByCategory(data);
      // Merge grouped tasks by category and dedupe
      setGroupedTasks(prev => {
        const byCategory = new Map(prev.map(g => [g.category, g]));
        for (const g of grouped) {
          const existing = byCategory.get(g.category);
          if (existing) {
            const seen = new Set(existing.tasks.map(t => t.id));
            const mergedTasks = [...existing.tasks, ...g.tasks.filter(t => !seen.has(t.id))];
            byCategory.set(g.category, {
              ...existing,
              id: existing.id ?? g.id,
              tasks: mergedTasks,
            });
          } else {
            byCategory.set(g.category, g);
          }
        }
        return Array.from(byCategory.values());
      });
    },
  });

  async function handleAllSelected() {
    if (!allCategoriesSelected) {
      setSelectedCategories(categories.map(category => category.id));

      const updatedCategories = categories.map(category => category.id);
      // updateAvailableTasks(updatedCategories, groupedTasks);
      await getTasksByCategory({
        url: "risk-job-task",
        fetchParams: {
          categoryId: [updatedCategories],
        },
      });
      return;
    }
    setSelectedCategories([]);
    handleChange("job_task", []);
    setAvailableTasks([]);
  }

  function handleAllTasksSelected() {
    setAllTasksSelected(prev => {
      const next = !prev;

      if (next) {
        // Select all currently available tasks
        setSelectedTasks(availableTasks);
        handleChange(
          "job_task",
          availableTasks.map(t => t.id)
        );
      } else {
        // Deselect all tasks
        setSelectedTasks([]);
        handleChange("job_task", []);
      }
      return next;
    });
  }

  async function handleCategoriesChange(category: number) {
    await getTasksByCategory({
      url: "risk-job-task",
      fetchParams: {
        categoryId: [category],
      },
    });
  }

  function deriveAvailableTasks(
    groups: GroupedTasksByCategory[],
    selectedCategoryIds?: number[]
  ): RiskJobRecord[] {
    const filteredGroups =
      Array.isArray(selectedCategoryIds) && selectedCategoryIds.length > 0
        ? groups.filter(g => g.id != null && selectedCategoryIds.includes(g.id))
        : [];

    const seen = new Set<number>();
    const flat = filteredGroups.flatMap(g => g.tasks);
    if (flat.length === 0) {
      setSelectedTasks([]);
    }
    const unique = flat.filter(task => {
      if (seen.has(task.id)) return false;
      seen.add(task.id);
      return true;
    });

    return unique;
  }

  const groupTasksByCategory = (tasks: RiskJobRecord[]) => {
    const grouped: { [categoryName: string]: RiskJobRecord[] } = {};

    tasks.forEach(task => {
      if (task.category && task.category.length > 0) {
        task.category.forEach(cat => {
          if (!grouped[cat.name]) {
            grouped[cat.name] = [];
          }
          grouped[cat.name].push(task);
        });
      } else {
        // Handle tasks without categories
        if (!grouped["Uncategorized"]) {
          grouped["Uncategorized"] = [];
        }
        grouped["Uncategorized"].push(task);
      }
    });

    // Convert object to array format
    return Object.entries(grouped).map(([categoryName, tasks]) => ({
      category: categoryName,
      id: tasks[0]?.category?.find(cat => cat.name === categoryName)?.id || null,
      tasks: tasks,
    }));
  };

  return (
    <div>
      <p className={"mt-4"}>
        What job tasks will you be doing on the site? This can be updated and expanded once the site
        is created as well.
      </p>
      {clientCategoriesLoading ? (
        <div>
          <ModernLoader />
        </div>
      ) : (
        <>
          {categories.length > 0 ? (
            <>
              <Divider style={{ margin: "1rem 0" }} align="center">
                <h5>Select task categories</h5>
              </Divider>
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <Checkbox
                  checked={allCategoriesSelected}
                  onChange={async () => {
                    setAllCategoriesSelected(!allCategoriesSelected);
                    await handleAllSelected();
                  }}
                  label={"Select all"}
                />
              </div>
              <div className={styles["responsive-grid-3"]}>
                {categories.map(category => {
                  return (
                    <div key={category.id}>
                      <Checkbox
                        checked={selectedCategories.includes(category.id)}
                        label={category.name}
                        className={styles["check-item"]}
                        onChange={async val => {
                          if (val.target.checked) {
                            setSelectedCategories(prev => [...prev, category.id]);
                            await handleCategoriesChange(category.id);
                          } else {
                            setSelectedCategories(prev => {
                              const next = prev.filter(id => id !== category.id);

                              // Prune selectedTasks: keep only tasks that still map to at least one remaining selected category
                              setSelectedTasks(prevSelected => {
                                const nextSelectedTasks = prevSelected.filter(task => {
                                  const taskCatIds = (task.category ?? []).map(c => c.id);
                                  // Keep the task if it shares any category with the next selected categories
                                  return taskCatIds.some(id => next.includes(id));
                                });

                                // Reflect pruned IDs in the form
                                handleChange(
                                  "job_task",
                                  nextSelectedTasks.map(t => t.id)
                                );

                                return nextSelectedTasks;
                              });
                              setAllCategoriesSelected(false);
                              return next;
                            });
                          }
                        }}
                      />
                    </div>
                  );
                })}
              </div>
              <Divider style={{ margin: "1rem 0" }} align="center">
                <h5>Select specific tasks</h5>
              </Divider>
              {loadingCategoryTasks ? (
                <div>
                  <ModernLoader />
                </div>
              ) : availableTasks.length > 0 ? (
                <>
                  <div style={{ display: "flex", justifyContent: "flex-end" }}>
                    <Checkbox
                      checked={allTasksSelected}
                      onChange={() => {
                        handleAllTasksSelected();
                      }}
                      label={"Select all"}
                    />
                  </div>
                  <div className={styles["responsive-grid-3"]}>
                    {availableTasks.map(task => {
                      return (
                        <div key={task.id}>
                          <Checkbox
                            checked={selectedTasks.map(task => task.id).includes(task.id)}
                            label={task.name}
                            onChange={() => {
                              handleSelectionChange(task);
                            }}
                          />
                        </div>
                      );
                    })}
                  </div>
                </>
              ) : (
                <div>
                  <h3>Select at least one category to select tasks.</h3>
                </div>
              )}
            </>
          ) : (
            <div>
              <h3>No job tasks were found!</h3>
            </div>
          )}
        </>
      )}
    </div>
  );
}
